import React from "react";
import PropTypes from "prop-types";
import {
    Modal,
    Collapse,
    Progress,
    Row,
    Col,
    Icon,
    Avatar,
    Radio,
    Button,
    Empty,
    Spin,
    Tooltip,
    message
} from "antd";
import "styles/applicant-parameter-checker.css";

const { Panel } = Collapse;

/**
 * Parameter groups. A check belongs to the first group whose prefix matches its
 * key, so any new backend key lands in "Applicant Details" by default.
 */
const GROUPS = [
    { title: "Residence Address", prefix: "residence_address__", icon: "home" },
    { title: "Permanent Address", prefix: "permanent_address__", icon: "environment" },
    { title: "Applicant Details", prefix: null, icon: "idcard" }
];

const FILTERS = { PENDING: "pending", ALL: "all" };

const GREEN = "#52c41a";
const AMBER = "#faad14";

/** Stable per-applicant avatar colour, so the same applicant always looks the same. */
const AVATAR_COLORS = ["#1890ff", "#722ed1", "#eb2f96", "#13c2c2", "#fa8c16"];

const initials = name =>
    (name || "?")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(_ => _[0])
        .join("")
        .toUpperCase();

const groupChecks = checks => {
    const groups = GROUPS.map(group => ({ ...group, checks: [] }));
    checks.forEach(check => {
        const group =
            groups.find(_ => _.prefix && (check.key || "").startsWith(_.prefix)) ||
            groups.find(_ => !_.prefix);
        group.checks.push(check);
    });
    // Pending first inside each group — the missing data is what the user came for.
    groups.forEach(group =>
        group.checks.sort((a, b) => Number(a.present) - Number(b.present))
    );
    return groups.filter(_ => _.checks.length);
};

/** "Residence Address - Pincode" reads as just "Pincode" under its own heading. */
const shortLabel = (parameter, groupTitle) => {
    const prefix = `${groupTitle} - `;
    return parameter.startsWith(prefix) ? parameter.slice(prefix.length) : parameter;
};

const pendingOf = applicant => (applicant.checks || []).filter(_ => !_.present);

/**
 * Applicant level data availability check (associate_parameter_checkers) so credit
 * users can see, per applicant, which details are still pending.
 */
class ApplicantParameterCheckerModal extends React.Component {
    state = {
        filter: FILTERS.PENDING
    };

    onFilterChange = e => this.setState({ filter: e.target.value });

    /** Pending list as plain text, ready to paste into remarks or a chat. */
    copyPendingList = () => {
        const text = (this.props.applicants || [])
            .map(applicant => {
                const pending = pendingOf(applicant);
                return pending.length
                    ? `${applicant.name}:\n${pending.map(_ => `- ${_.parameter}`).join("\n")}`
                    : `${applicant.name}: all details captured`;
            })
            .join("\n\n");
        const input = document.createElement("textarea");
        input.value = text;
        document.body.appendChild(input);
        input.select();
        try {
            document.execCommand("copy");
            message.success("Pending details copied");
        } catch (e) {
            message.error("Could not copy, please select manually");
        }
        document.body.removeChild(input);
    };

    renderPill = (check, groupTitle) => (
        <Tooltip key={check.key} title={check.parameter}>
            <span
                className={`apc-pill ${check.present ? "apc-pill-present" : "apc-pill-pending"}`}
            >
                <Icon type={check.present ? "check" : "exclamation"} />
                {shortLabel(check.parameter, groupTitle)}
            </span>
        </Tooltip>
    );

    renderGroup = group => {
        const visible =
            this.state.filter === FILTERS.PENDING
                ? group.checks.filter(_ => !_.present)
                : group.checks;
        if (!visible.length) {
            return null;
        }
        const pending = group.checks.filter(_ => !_.present).length;
        return (
            <div className={"apc-group"} key={group.title}>
                <div className={"apc-group-header"}>
                    <Icon type={group.icon} style={{ color: "#8c8c8c" }} />
                    <span className={"apc-group-title"}>{group.title}</span>
                    <span
                        className={"apc-group-count"}
                        style={{ color: pending ? "#cf1322" : "#389e0d" }}
                    >
                        {pending ? `${pending} missing` : `all ${group.checks.length} captured`}
                    </span>
                    <span className={"apc-group-rule"} />
                </div>
                <div className={"apc-pills"}>
                    {visible.map(check => this.renderPill(check, group.title))}
                </div>
            </div>
        );
    };

    renderApplicantHeader = (applicant, index) => {
        const checks = applicant.checks || [];
        const pending = pendingOf(applicant).length;
        const captured = checks.length - pending;
        const percent = checks.length ? Math.round((captured / checks.length) * 100) : 100;
        return (
            <Row type={"flex"} align={"middle"} gutter={12} style={{ flexWrap: "nowrap" }}>
                <Col>
                    <Avatar
                        style={{
                            backgroundColor: AVATAR_COLORS[index % AVATAR_COLORS.length]
                        }}
                    >
                        {initials(applicant.name)}
                    </Avatar>
                </Col>
                <Col style={{ flex: 1, minWidth: 0 }}>
                    <div>
                        <span className={"apc-applicant-name"}>{applicant.name}</span>
                        <span className={"apc-applicant-id"}>ID {applicant.associate_id}</span>
                    </div>
                    <Progress
                        className={"apc-applicant-progress"}
                        percent={percent}
                        showInfo={false}
                        strokeWidth={6}
                        strokeColor={pending ? AMBER : GREEN}
                    />
                </Col>
                <Col style={{ textAlign: "right", minWidth: 110 }}>
                    <div
                        className={"apc-applicant-status"}
                        style={{ color: pending ? "#cf1322" : "#389e0d" }}
                    >
                        {pending ? `${pending} pending` : "Complete"}
                    </div>
                    <div className={"apc-applicant-count"}>
                        {captured} of {checks.length} captured
                    </div>
                </Col>
            </Row>
        );
    };

    renderApplicant = (applicant, index) => {
        const pending = pendingOf(applicant).length;
        const groups = groupChecks(applicant.checks || []);
        return (
            <Panel
                key={String(applicant.associate_id)}
                header={this.renderApplicantHeader(applicant, index)}
            >
                {pending || this.state.filter === FILTERS.ALL ? (
                    groups.map(this.renderGroup)
                ) : (
                    <div className={"apc-complete"}>
                        <Icon type={"check-circle"} theme={"filled"} /> All details captured for
                        this applicant
                    </div>
                )}
            </Panel>
        );
    };

    render() {
        const { visible, onCancel, applicants, loading } = this.props;
        const list = applicants || [];
        const totalChecks = list.reduce((n, _) => n + (_.checks || []).length, 0);
        const totalPending = list.reduce((n, _) => n + pendingOf(_).length, 0);
        const overall = totalChecks
            ? Math.round(((totalChecks - totalPending) / totalChecks) * 100)
            : 100;
        return (
            <Modal
                className={"apc-modal"}
                title={
                    <span>
                        <Icon type={"solution"} className={"mr-10"} />
                        Applicant Details Check
                    </span>
                }
                visible={visible}
                onCancel={onCancel}
                width={800}
                footer={null}
                destroyOnClose={true}
            >
                <Spin spinning={!!loading}>
                    <Row
                        className={"apc-summary"}
                        type={"flex"}
                        align={"middle"}
                        justify={"space-between"}
                        gutter={12}
                    >
                        <Col>
                            <Row type={"flex"} align={"middle"} gutter={16}>
                                <Col>
                                    <Progress
                                        type={"circle"}
                                        width={52}
                                        percent={overall}
                                        strokeColor={totalPending ? AMBER : GREEN}
                                    />
                                </Col>
                                <Col>
                                    <div className={"apc-summary-title"}>
                                        {totalPending
                                            ? `${totalPending} detail(s) pending`
                                            : "All details captured"}
                                    </div>
                                    <div className={"apc-summary-sub"}>
                                        across {list.length} applicant(s) ·{" "}
                                        {totalChecks - totalPending} of {totalChecks} parameters
                                        captured
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                        <Col>
                            <Radio.Group
                                value={this.state.filter}
                                onChange={this.onFilterChange}
                                size={"small"}
                                className={"mr-10"}
                            >
                                <Radio.Button value={FILTERS.PENDING}>Pending</Radio.Button>
                                <Radio.Button value={FILTERS.ALL}>All</Radio.Button>
                            </Radio.Group>
                            <Button
                                size={"small"}
                                icon={"copy"}
                                disabled={!totalPending}
                                onClick={this.copyPendingList}
                            >
                                Copy pending
                            </Button>
                        </Col>
                    </Row>
                    {list.length ? (
                        <Collapse
                            className={"apc-collapse"}
                            bordered={false}
                            defaultActiveKey={list
                                .filter(_ => pendingOf(_).length)
                                .map(_ => String(_.associate_id))}
                            expandIconPosition={"right"}
                        >
                            {list.map(this.renderApplicant)}
                        </Collapse>
                    ) : (
                        <Empty description={"No applicant details found"} />
                    )}
                </Spin>
            </Modal>
        );
    }
}

ApplicantParameterCheckerModal.propTypes = {
    visible: PropTypes.bool,
    loading: PropTypes.bool,
    onCancel: PropTypes.func,
    applicants: PropTypes.array
};

export default ApplicantParameterCheckerModal;
